/**
 * @fileoverview Shared draft → publish → archive lifecycle controls for the document
 * template builder (status pill + Save + Publish + Archive).
 * @module ui-admin/components/DocumentLifecycleControls
 *
 * Responsibilities: - Render the single-author publish lifecycle chrome (Decision D13-R):
 *                     a status pill plus the Save, Publish, and Archive actions, identically
 *                     for tenant-web AND system-web so the lifecycle never drifts between apps.
 * Dependencies:     - lucide-react (Save / Send / Archive icons). No data access.
 * Architecture:     - Presentation-only, fully controlled: the host owns template state and the
 *                     save/publish/archive callbacks (each wired to its own local-first service).
 *                     This component renders nothing app-specific and performs no I/O.
 * Notes:            - Publish shows only for a saved (`!isNew`) `draft`; Archive only for a saved
 *                     `published` template — matching the prior tenant-web behavior exactly.
 */

import { Save, Send, Archive } from 'lucide-react';

/** Publish lifecycle status of a document template (Decision D13-R). */
export type DocumentTemplateStatus = 'draft' | 'published' | 'archived';

/**
 * @contract DocumentLifecycleControlsProps
 * Purpose: Controlled prop surface for the shared lifecycle controls.
 * Fields:
 *  - status: current template status; omitted (undefined) for an unsaved new template.
 *  - saving / publishing: in-flight flags that disable + relabel the matching button.
 *  - isViewMode: when true, only the status pill renders (no mutating actions).
 *  - isNew: when true, Publish/Archive are hidden (template must be saved first).
 *  - onSave / onPublish / onArchive: host callbacks wired to its local-first service.
 *  - accentColor: optional Save-button background (host theme accent).
 *  - saveDataTour / saveDataTourSections: optional data-tour-* attrs for guided tours.
 * Rules:
 *  - Publish renders only when `!isViewMode && !isNew && status === 'draft'`.
 *  - Archive renders only when `!isViewMode && !isNew && status === 'published'`.
 */
export interface DocumentLifecycleControlsProps {
  status?: DocumentTemplateStatus;
  saving?: boolean;
  publishing?: boolean;
  isViewMode?: boolean;
  isNew?: boolean;
  onSave: () => void;
  onPublish: () => void;
  onArchive: () => void;
  accentColor?: string;
  saveDataTour?: string;
  saveDataTourSections?: string;
}

const STATUS_PILL: Record<DocumentTemplateStatus, string> = {
  published: 'bg-green-100 text-green-700',
  archived: 'bg-amber-100 text-amber-700',
  draft: 'bg-gray-100 text-gray-700',
};

/**
 * @component DocumentLifecycleControls
 * Purpose:     Render the status pill and Save / Publish / Archive actions shared by both
 *              document builder pages, guaranteeing identical lifecycle UX (G-PARITY).
 * Props:       DocumentLifecycleControlsProps (fully controlled).
 * Behavior:    Publish appears for saved drafts; Archive for published templates; the pill
 *              always reflects the current status.
 * Data Source: none (presentation only).
 * Constraints: no data access, no I/O; host owns all state and callbacks.
 * Performance: trivial; renders a few buttons.
 */
export function DocumentLifecycleControls({
  status,
  saving = false,
  publishing = false,
  isViewMode = false,
  isNew = false,
  onSave,
  onPublish,
  onArchive,
  accentColor,
  saveDataTour,
  saveDataTourSections,
}: DocumentLifecycleControlsProps) {
  return (
    <>
      {status && (
        <span
          data-testid="lifecycle-status-pill"
          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_PILL[status]}`}
        >
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      )}

      {!isViewMode && (
        <button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="lifecycle-save"
          data-tour={saveDataTour}
          data-tour-sections={saveDataTourSections}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg transition-colors disabled:opacity-50"
          style={accentColor ? { backgroundColor: accentColor } : undefined}
        >
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : 'Save'}
        </button>
      )}

      {!isViewMode && !isNew && status === 'draft' && (
        <button
          type="button"
          onClick={onPublish}
          disabled={publishing}
          data-testid="lifecycle-publish"
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          <Send className="h-4 w-4" />
          {publishing ? 'Publishing...' : 'Publish'}
        </button>
      )}

      {!isViewMode && !isNew && status === 'published' && (
        <button
          type="button"
          onClick={onArchive}
          data-testid="lifecycle-archive"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-lg hover:bg-amber-100 transition-colors"
        >
          <Archive className="h-4 w-4" />
          Archive
        </button>
      )}
    </>
  );
}

export default DocumentLifecycleControls;
